import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  endsWith,
  get,
  includes,
  isArray,
  isString,
  map,
  replace,
  toLower,
  trim,
} from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { gql, useQuery } from '@apollo/client';

import api from 'api';
import browserHistory from 'app/history';
import getHelpfulErrorMessage from 'utils/getHelpfulErrorMessage';
import countCsvDataRows from 'utils/countCsvDataRows';
import {
  getMaxSubscriptionImportDataRows,
  isPresignedSubscriptionImportUpload,
  postSubscriptionImportCsvToS3,
  SUBSCRIPTION_IMPORT_ROW_LIMIT_ERROR_CODE,
} from 'utils/subscriptionImportPresignedUpload';

const ALLOWED_MIME_TYPES = ['text/csv', 'application/vnd.ms-excel'];
const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024;
const POLL_INTERVAL_MS = 3000;
const IMPORT_STATUS_404_RETRIES = 5;

const SAMPLE_SUBSCRIPTION_IMPORT_TABLE = {
  headers: ['customer_id', 'amount', 'currency', 'start_date', 'frequency', 'description'],
  rows: [
    ['CU000ZBFGXG1X9', '10', 'GBP', '2020-10-25', 'monthly', 'Donation'],
    ['CU000V3WXDQR52', '5', 'GBP', '2020-10-23', 'monthly', 'Membership'],
  ],
};

const GET_ACCOUNT_FOR_MIGRATION_ROUTE = gql`
  query GetAccountForMigrationRoute($accountId: Int!) {
    account(id: $accountId) {
      id
      name
      slug
    }
  }
`;

const SUBSCRIPTION_IMPORT_ERROR_CODE_LABELS = {
  import_not_completed: 'The import is still running. Open the download link after it finishes.',
  import_run_not_found: 'That import run was not found. It may have expired or the ID is wrong.',
  validation_run_not_found: 'That validation run was not found. It may have expired or the ID is wrong.',
  validate_failed: 'Validation could not be completed. Fix the file or try again.',
};

const hasRowValidationErrors = (validation) => (
  isArray(validation?.row_errors) && validation.row_errors.length > 0
);

const getProgressValue = (statusResponse) => {
  const progress = Number(
    statusResponse?.progress
    || statusResponse?.job?.progress
    || statusResponse?.entityImport?.progress
    || 0,
  );
  if (Number.isNaN(progress)) {
    return 0;
  }
  if (progress < 0) {
    return 0;
  }
  if (progress > 100) {
    return 100;
  }
  return progress;
};

const getStatusLabel = (statusResponse) => (
  statusResponse?.status
  || statusResponse?.job?.status
  || statusResponse?.entityImport?.status
  || 'running'
);

const formatSubscriptionImportApiError = (err) => {
  const message = get(err, 'error.message');
  if (message) {
    return message;
  }
  const code = get(err, 'error.code');
  if (code && SUBSCRIPTION_IMPORT_ERROR_CODE_LABELS[code]) {
    return SUBSCRIPTION_IMPORT_ERROR_CODE_LABELS[code];
  }
  if (code) {
    return replace(code, /_/g, ' ');
  }
  return getHelpfulErrorMessage(err);
};

const getFailureMessageFromStatus = (statusResponse) => {
  const message = get(statusResponse, 'error.message')
    || (isString(statusResponse?.error) ? statusResponse.error : null);
  if (message) {
    return message;
  }
  const code = get(statusResponse, 'error.code')
    || statusResponse?.status;
  if (code && SUBSCRIPTION_IMPORT_ERROR_CODE_LABELS[code]) {
    return SUBSCRIPTION_IMPORT_ERROR_CODE_LABELS[code];
  }
  if (code) {
    return `Import failed (${code}).`;
  }
  return 'Import failed.';
};

// The payments-service writes one of three terminal/in-flight statuses to S3
// for both validate and import jobs: 'processing', 'completed', 'failed'.
const SUBSCRIPTION_IMPORT_JOB_STATUS = {
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

const getJobStatus = (statusResponse) => toLower(String(statusResponse?.status || ''));

const isJobProcessing = (statusResponse) => (
  getJobStatus(statusResponse) === SUBSCRIPTION_IMPORT_JOB_STATUS.PROCESSING
);

const isJobCompleted = (statusResponse) => (
  getJobStatus(statusResponse) === SUBSCRIPTION_IMPORT_JOB_STATUS.COMPLETED
);

const isJobFailed = (statusResponse) => (
  getJobStatus(statusResponse) === SUBSCRIPTION_IMPORT_JOB_STATUS.FAILED
);

const getValidateFailureMessageFromStatus = (statusResponse) => {
  const message = get(statusResponse, 'error.message')
    || (isString(statusResponse?.error) ? statusResponse.error : null);
  if (message) {
    return message;
  }
  const code = get(statusResponse, 'error.code')
    || statusResponse?.status;
  if (code && SUBSCRIPTION_IMPORT_ERROR_CODE_LABELS[code]) {
    return SUBSCRIPTION_IMPORT_ERROR_CODE_LABELS[code];
  }
  if (code) {
    return `Validation failed (${code}).`;
  }
  return 'Validation failed.';
};

const getSummaryFromPayload = (payload) => payload?.summary ?? null;

const getDownloadUrlFromPayload = (payload) => payload?.download_url ?? null;

const GoCardlessMigration = ({ classes, match }) => {
  const routeAccountId = match.params.account_id != null
    ? Number(match.params.account_id)
    : NaN;
  const hasRouteAccountId = Number.isInteger(routeAccountId) && routeAccountId > 0;

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [importRunId, setImportRunId] = useState(null);
  const [validation, setValidation] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [validationErrorCode, setValidationErrorCode] = useState(null);
  const [validationRowEstimateWarning, setValidationRowEstimateWarning] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImportStarting, setIsImportStarting] = useState(false);
  const [isImportPolling, setIsImportPolling] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importErrorCode, setImportErrorCode] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  // Recent migrations list (disabled)
  // const [recentRuns, setRecentRuns] = useState([]);
  // const [recentRunsError, setRecentRunsError] = useState(null);
  const [validatePollStatus, setValidatePollStatus] = useState(null);
  /** Set after validate POST succeeds following presigned upload; required for the import API. */
  const [importInputCsvS3, setImportInputCsvS3] = useState(null);
  const [sampleCsvDialogOpen, setSampleCsvDialogOpen] = useState(false);

  const accountSearchTrimmed = trim(accountSearchQuery);
  const accountSearchId = /^\d+$/.test(accountSearchTrimmed)
    ? Number(accountSearchTrimmed)
    : NaN;
  const isValidAccountSearchId = Number.isInteger(accountSearchId) && accountSearchId > 0;
  const hasInvalidAccountSearchInput = (
    accountSearchTrimmed.length > 0 && !isValidAccountSearchId
  );

  const {
    data: searchAccountData,
    loading: searchLoading,
    error: searchError,
  } = useQuery(GET_ACCOUNT_FOR_MIGRATION_ROUTE, {
    variables: { accountId: accountSearchId },
    skip: !isValidAccountSearchId || Boolean(selectedAccount) || hasRouteAccountId,
  });

  const { data: routeAccountData } = useQuery(GET_ACCOUNT_FOR_MIGRATION_ROUTE, {
    variables: { accountId: routeAccountId },
    skip: !hasRouteAccountId,
  });

  useEffect(() => {
    if (!hasRouteAccountId || !routeAccountData?.account) {
      return;
    }
    const a = routeAccountData.account;
    setSelectedAccount({
      id: Number(a.id),
      name: a.name,
      slug: a.slug != null ? String(a.slug) : '',
    });
  }, [hasRouteAccountId, routeAccountData]);

  const accountId = selectedAccount?.id;
  const hasAccountContext = Number.isInteger(accountId) && accountId > 0;

  const searchHit = searchAccountData?.account;

  const importPollTimerRef = useRef(null);
  const validatePollTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  /**
   * Increments when the user resets/changes context or starts a new validate/import.
   * Used to ignore late HTTP responses from an older try (not the backend import_run_id).
   */
  const migrationUiAttemptIdRef = useRef(0);
  const importStatus404PollsRef = useRef(0);

  const stopImportPolling = useCallback(() => {
    if (importPollTimerRef.current) {
      clearInterval(importPollTimerRef.current);
      importPollTimerRef.current = null;
    }
    setIsImportPolling(false);
  }, []);

  const stopValidatePolling = useCallback(() => {
    if (validatePollTimerRef.current) {
      clearInterval(validatePollTimerRef.current);
      validatePollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    stopImportPolling();
    stopValidatePolling();
  }, [stopImportPolling, stopValidatePolling]);

  const resetImportState = useCallback(() => {
    migrationUiAttemptIdRef.current += 1;
    importStatus404PollsRef.current = 0;
    stopImportPolling();
    stopValidatePolling();
    setIsValidating(false);
    setIsImportStarting(false);
    setValidation(null);
    setValidationError(null);
    setValidationErrorCode(null);
    setValidationRowEstimateWarning(null);
    setValidatePollStatus(null);
    setImportStatus(null);
    setImportSummary(null);
    setImportError(null);
    setImportErrorCode(null);
    setDownloadUrl(null);
    setDownloadError(null);
    setImportInputCsvS3(null);
    setImportRunId(null);
  }, [stopImportPolling, stopValidatePolling]);

  const handleChangeAccount = useCallback(() => {
    setSelectedAccount(null);
    resetImportState();
    setFile(null);
    setFileError(null);
    if (hasRouteAccountId) {
      // eslint-disable-next-line lodash/prefer-lodash-method -- history.replace(), not String#replace
      browserHistory.replace('/migrations');
    }
  }, [hasRouteAccountId, resetImportState]);

  const handleSelectSearchHit = useCallback(() => {
    if (!searchHit) {
      return;
    }
    resetImportState();
    setFileError(null);
    setSelectedAccount({
      id: Number(searchHit.id),
      name: searchHit.name,
      slug: searchHit.slug != null ? String(searchHit.slug) : '',
    });
  }, [resetImportState, searchHit]);

  // Recent migrations list (disabled)
  // const fetchRecentRuns = useCallback(async () => {
  //   try {
  //     setRecentRunsError(null);
  //     const response = await api.get(`?days=30`);
  //     const runs = response?.runs || response?.results || [];
  //     if (isArray(runs)) {
  //       setRecentRuns(runs);
  //     }
  //   } catch (error) {
  //     setRecentRunsError(formatSubscriptionImportApiError(error));
  //   }
  // }, []);

  // useEffect(() => {
  //   fetchRecentRuns();
  // }, [fetchRecentRuns]);

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files && e.target.files[0];
    setFile(uploadedFile || null);
    setFileError(null);
    resetImportState();
  };

  const validateSelectedFile = () => {
    if (!file) {
      setFileError('Please select a CSV file first.');
      return false;
    }

    const hasCsvMimeType = includes(ALLOWED_MIME_TYPES, file.type);
    const hasCsvExtension = endsWith(toLower(file.name), '.csv');
    if (!hasCsvMimeType && !hasCsvExtension) {
      setFileError('Please upload a CSV file.');
      return false;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError('CSV file is too large. Max size is 15MB.');
      return false;
    }

    setFileError(null);
    return true;
  };

  const loadDownloadUrl = useCallback(async (runId, { reportError = false, attemptIdWhenStarted } = {}) => {
    try {
      if (reportError) {
        setDownloadError(null);
      }
      const response = await api.get(
        `/account/${accountId}/bulk_subscription_import/${runId}/download`,
      );
      if (
        attemptIdWhenStarted != null
        && attemptIdWhenStarted !== migrationUiAttemptIdRef.current
      ) {
        return;
      }
      const url = getDownloadUrlFromPayload(response);
      if (url) {
        setDownloadUrl(url);
      } else if (reportError) {
        setDownloadError('No download URL was returned.');
      }
    } catch (error) {
      if (
        attemptIdWhenStarted != null
        && attemptIdWhenStarted !== migrationUiAttemptIdRef.current
      ) {
        return;
      }
      if (reportError) {
        setDownloadError(formatSubscriptionImportApiError(error));
      }
    }
  }, [accountId]);

  const handleImportFailed = useCallback((statusResponse) => {
    setImportStatus(statusResponse);
    setImportSummary(null);
    setDownloadUrl(null);
    setDownloadError(null);
    setImportError(getFailureMessageFromStatus(statusResponse));
    setImportErrorCode(get(statusResponse, 'error.code') || null);
    stopImportPolling();
    // fetchRecentRuns();
  }, [stopImportPolling]);

  const handleImportSuccess = useCallback(async (payload, runId) => {
    const attemptIdWhenStarted = migrationUiAttemptIdRef.current;
    setImportStatus(payload);
    setImportSummary(getSummaryFromPayload(payload));
    setDownloadUrl(null);
    setDownloadError(null);
    setImportError(null);
    setImportErrorCode(null);
    stopImportPolling();
    // fetchRecentRuns();
    await loadDownloadUrl(runId, { reportError: true, attemptIdWhenStarted });
  }, [loadDownloadUrl, stopImportPolling]);

  const pollImportStatus = useCallback(async (runId) => {
    const attemptIdWhenStarted = migrationUiAttemptIdRef.current;
    try {
      const statusResponse = await api.get(
        `/account/${accountId}/bulk_subscription_import/${runId}/status`,
      );
      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return false;
      }
      importStatus404PollsRef.current = 0;
      setImportStatus(statusResponse);
      if (isJobCompleted(statusResponse)) {
        setImportError(null);
        setImportErrorCode(null);
        await handleImportSuccess(statusResponse, runId);
        return false;
      }
      if (isJobFailed(statusResponse)) {
        handleImportFailed(statusResponse);
        return false;
      }
      setImportError(null);
      setImportErrorCode(null);
      return true;
    } catch (error) {
      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return false;
      }
      if (get(error, 'httpStatus') === 404) {
        importStatus404PollsRef.current += 1;
        if (importStatus404PollsRef.current <= IMPORT_STATUS_404_RETRIES) {
          return true;
        }
      }
      setImportError(formatSubscriptionImportApiError(error));
      setImportErrorCode(get(error, 'error.code') || null);
      stopImportPolling();
      return false;
    }
  }, [accountId, handleImportFailed, handleImportSuccess, stopImportPolling]);

  const startImportPolling = useCallback((runId) => {
    stopImportPolling();
    setIsImportPolling(true);
    importPollTimerRef.current = setInterval(() => {
      pollImportStatus(runId);
    }, POLL_INTERVAL_MS);
  }, [pollImportStatus, stopImportPolling]);

  const pollValidateStatus = useCallback(async (runId) => {
    const attemptIdWhenStarted = migrationUiAttemptIdRef.current;
    try {
      const statusResponse = await api.get(
        `/account/${accountId}/bulk_subscription_import/${runId}/validate_status`,
      );
      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return false;
      }
      setValidatePollStatus(statusResponse);

      if (isJobFailed(statusResponse)) {
        stopValidatePolling();
        setValidatePollStatus(null);
        setValidation(null);
        setImportInputCsvS3(null);
        setValidationError(getValidateFailureMessageFromStatus(statusResponse));
        setValidationErrorCode(get(statusResponse, 'error.code') || null);
        setIsValidating(false);
        return false;
      }

      if (isJobCompleted(statusResponse)) {
        stopValidatePolling();
        setValidatePollStatus(null);
        setValidationError(null);
        setValidationErrorCode(null);
        setValidation(statusResponse);
        setIsValidating(false);
        return false;
      }

      if (!isJobProcessing(statusResponse)) {
        stopValidatePolling();
        setValidatePollStatus(null);
        setValidation(null);
        setImportInputCsvS3(null);
        setValidationError(
          getValidateFailureMessageFromStatus(statusResponse)
          || 'Unexpected validation status. Please try again.',
        );
        setValidationErrorCode(get(statusResponse, 'error.code') || null);
        setIsValidating(false);
        return false;
      }

      return true;
    } catch (error) {
      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return false;
      }
      stopValidatePolling();
      setValidatePollStatus(null);
      setValidation(null);
      setImportInputCsvS3(null);
      setValidationError(formatSubscriptionImportApiError(error));
      setValidationErrorCode(get(error, 'error.code') || null);
      setIsValidating(false);
      return false;
    }
  }, [accountId, stopValidatePolling]);

  const startValidatePolling = useCallback((runId) => {
    stopValidatePolling();
    validatePollTimerRef.current = setInterval(() => {
      pollValidateStatus(runId);
    }, POLL_INTERVAL_MS);
  }, [pollValidateStatus, stopValidatePolling]);

  const handleValidate = async () => {
    const isValidFile = validateSelectedFile();
    if (!isValidFile) {
      return;
    }

    if (!hasAccountContext) {
      setValidationError(
        'Look up an account by ID above and confirm it before validating.',
      );
      setValidationErrorCode(null);
      return;
    }

    migrationUiAttemptIdRef.current += 1;
    const attemptIdWhenStarted = migrationUiAttemptIdRef.current;
    try {
      setIsValidating(true);
      setValidation(null);
      setValidationError(null);
      setValidationErrorCode(null);
      setValidationRowEstimateWarning(null);
      setValidatePollStatus(null);
      setImportError(null);
      setImportErrorCode(null);
      setImportSummary(null);
      setDownloadUrl(null);
      setDownloadError(null);
      setImportInputCsvS3(null);
      stopValidatePolling();

      const presignPayload = await api.get(
        `/account/${accountId}/bulk_subscription_import/upload_url`,
      );

      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return;
      }

      if (!isPresignedSubscriptionImportUpload(presignPayload)) {
        setValidationError(
          'CSV validation requires presigned S3 upload credentials from the server, but none were returned. Try again or contact support.',
        );
        setValidationErrorCode(null);
        setIsValidating(false);
        return;
      }

      const runId = presignPayload.import_run_id;
      if (!isString(runId) || !runId) {
        setValidationError(
          'Upload response did not include import_run_id. Try again or contact support.',
        );
        setValidationErrorCode(null);
        setIsValidating(false);
        return;
      }
      setImportRunId(runId);

      const maxUploadBytes = Number(presignPayload.max_upload_bytes);
      const uploadCap = Number.isFinite(maxUploadBytes) && maxUploadBytes > 0
        ? maxUploadBytes
        : MAX_FILE_SIZE_BYTES;

      if (file.size > uploadCap) {
        const capMb = Math.max(1, Math.round(uploadCap / (1024 * 1024)));
        setValidationError(`CSV file is too large for this import. Maximum upload size is ${capMb}MB.`);
        setValidationErrorCode(null);
        setIsValidating(false);
        return;
      }

      const maxDataRows = getMaxSubscriptionImportDataRows(presignPayload);
      if (maxDataRows != null) {
        const dataRows = await countCsvDataRows(file);
        if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
          return;
        }
        if (dataRows > maxDataRows) {
          setValidationRowEstimateWarning(
            `This file has about ${dataRows.toLocaleString()} data rows (excluding the header), which exceeds the maximum allowed for import (${maxDataRows.toLocaleString()}). Reduce the number of rows or split the file, then try again.`,
          );
          setIsValidating(false);
          return;
        }
      }

      try {
        await postSubscriptionImportCsvToS3(presignPayload, file);
      } catch (uploadErr) {
        if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
          return;
        }
        setValidationError(
          uploadErr.message || 'Uploading the CSV failed. Please try again.',
        );
        setValidationErrorCode(null);
        setIsValidating(false);
        return;
      }

      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return;
      }

      await api.post(`/account/${accountId}/bulk_subscription_import/validate`, {
        import_run_id: runId,
        csv_s3: {
          bucket: presignPayload.bucket,
          key: presignPayload.key,
        },
      });

      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return;
      }

      setImportInputCsvS3({
        bucket: presignPayload.bucket,
        key: presignPayload.key,
      });

      const stillPending = await pollValidateStatus(runId);
      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return;
      }
      if (stillPending) {
        startValidatePolling(runId);
      }
    } catch (error) {
      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return;
      }
      setValidationError(formatSubscriptionImportApiError(error));
      setValidationErrorCode(get(error, 'error.code') || null);
      setIsValidating(false);
    }
  };

  const handleImportStart = async () => {
    if (!validation?.valid || !importRunId || !hasAccountContext) {
      return;
    }
    if (!importInputCsvS3?.bucket || !importInputCsvS3?.key) {
      setImportError('Missing CSV upload location. Run validation again after uploading your CSV.');
      setImportErrorCode(null);
      return;
    }

    migrationUiAttemptIdRef.current += 1;
    const attemptIdWhenStarted = migrationUiAttemptIdRef.current;
    importStatus404PollsRef.current = 0;

    try {
      setIsImportStarting(true);
      setImportError(null);
      setImportErrorCode(null);
      setDownloadError(null);
      setImportSummary(null);
      setDownloadUrl(null);

      const response = await api.post(`/account/${accountId}/bulk_subscription_import/import`, {
        import_run_id: importRunId,
        csv_s3: {
          bucket: importInputCsvS3.bucket,
          key: importInputCsvS3.key,
        },
      });

      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return;
      }

      setImportStatus(response);
      const stillRunning = await pollImportStatus(importRunId);
      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return;
      }
      if (stillRunning !== false) {
        startImportPolling(importRunId);
      }
    } catch (error) {
      if (attemptIdWhenStarted !== migrationUiAttemptIdRef.current) {
        return;
      }
      setImportError(formatSubscriptionImportApiError(error));
      setImportErrorCode(get(error, 'error.code') || null);
    } finally {
      setIsImportStarting(false);
    }
  };

  const canStartImport = useMemo(
    () => Boolean(
      hasAccountContext
      && validation?.valid
      && !hasRowValidationErrors(validation)
      && importInputCsvS3?.bucket
      && importInputCsvS3?.key
      && !isImportStarting,
    ),
    [hasAccountContext, importInputCsvS3, isImportStarting, validation],
  );
  const hasSuccessfulValidation = useMemo(
    () => Boolean(validation?.valid && !hasRowValidationErrors(validation)),
    [validation],
  );
  const hasImportActivity = useMemo(
    () => Boolean(isImportPolling || importStatus || importSummary || importError || downloadError),
    [downloadError, importError, importStatus, importSummary, isImportPolling],
  );
  const hasImportStarted = useMemo(
    () => Boolean(isImportStarting || hasImportActivity),
    [hasImportActivity, isImportStarting],
  );
  const isContextLocked = useMemo(
    () => Boolean(hasSuccessfulValidation || hasImportStarted),
    [hasImportStarted, hasSuccessfulValidation],
  );
  const hasWorkflowState = useMemo(
    () => Boolean(selectedAccount || file || validation || hasImportStarted || validationError || fileError),
    [file, fileError, hasImportStarted, selectedAccount, validation, validationError],
  );

  const rowErrorMessage = useMemo(() => {
    if (!hasRowValidationErrors(validation)) {
      return null;
    }
    return map(validation.row_errors, (rowError) => `Row ${rowError.row}: ${rowError.message}`)
      .join(' | ');
  }, [validation]);

  const migrationProgressBarValue = useMemo(() => {
    if (importSummary || importError) {
      return 100;
    }
    return getProgressValue(importStatus);
  }, [importError, importSummary, importStatus]);

  const handleStartOver = useCallback(() => {
    setSelectedAccount(null);
    setAccountSearchQuery('');
    setFile(null);
    setFileError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    resetImportState();
    if (hasRouteAccountId) {
      // eslint-disable-next-line lodash/prefer-lodash-method -- history.replace(), not String#replace
      browserHistory.replace('/migrations');
    }
  }, [hasRouteAccountId, resetImportState]);

  return (
    <div>
      <Typography variant="h6">
        Migrations
      </Typography>

      <Card className={classes.sectionCard}>
        <CardContent>
          <div className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              GoCardless subscription import
            </Typography>
            {hasWorkflowState && (
              <Button
                size="small"
                color="default"
                onClick={handleStartOver}
              >
                Start over
              </Button>
            )}
          </div>

          <Typography variant="body2" color="textSecondary">
            Upload a CSV, run validation, then start the migration when validation passes.
          </Typography>

          <Typography variant="subtitle2" className={classes.accountSearchHeading}>
            For which account are you running this import?
          </Typography>
          <TextField
            label="Account ID"
            placeholder="e.g. 12345"
            value={accountSearchQuery}
            onChange={(e) => setAccountSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            className={classes.accountSearchField}
            disabled={Boolean(selectedAccount) || isContextLocked}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />

          {!selectedAccount && hasInvalidAccountSearchInput && (
            <Typography variant="caption" color="textSecondary" display="block" className={classes.accountSearchHint}>
              Enter digits only (Beacon account ID).
            </Typography>
          )}

          {selectedAccount && (
            <div className={classes.messageSpacing}>
              <Card variant="outlined" className={classes.selectedAccountCard}>
                <CardContent className={classes.selectedAccountContent}>
                  <div className={classes.selectedAccountHeader}>
                    <Typography variant="caption" className={classes.selectedAccountLabel}>
                      Selected account
                    </Typography>
                    {!isContextLocked && (
                      <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        className={classes.changeAccountButton}
                        onClick={handleChangeAccount}
                      >
                        Change account
                      </Button>
                    )}
                  </div>
                  <Typography variant="body2" component="div">
                    <strong>ID</strong>
                    {': '}
                    {selectedAccount.id}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>Name</strong>
                    {': '}
                    {selectedAccount.name}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>Slug</strong>
                    {': '}
                    {selectedAccount.slug || '—'}
                  </Typography>
                </CardContent>
              </Card>
            </div>
          )}

          {!selectedAccount && isValidAccountSearchId && searchLoading && (
            <div className={classes.accountSearchStatus}>
              <CircularProgress size={22} className={classes.accountSearchSpinner} />
            </div>
          )}

          {searchError && (
            <div className={classes.messageSpacing}>
              <Alert severity="error">
                {get(searchError, 'message') || 'Could not load that account.'}
              </Alert>
            </div>
          )}

          {!selectedAccount && isValidAccountSearchId && !searchLoading && !searchHit && !searchError && (
            <div className={classes.messageSpacing}>
              <Alert severity="warning">No account found with that ID.</Alert>
            </div>
          )}

          {!selectedAccount && isValidAccountSearchId && !searchLoading && searchHit && (
            <Card variant="outlined" className={classes.accountSearchResult}>
              <CardContent>
                <Typography variant="body2">
                  <strong>ID</strong>
                  {': '}
                  {searchHit.id}
                </Typography>
                <Typography variant="body2">
                  <strong>Name</strong>
                  {': '}
                  {searchHit.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Slug</strong>
                  {': '}
                  {searchHit.slug || '—'}
                </Typography>
                <div className={classes.actionRow}>
                  <Button variant="contained" color="primary" onClick={handleSelectSearchHit}>
                    Use this account
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className={classes.inputRow}>
            <input
              ref={fileInputRef}
              accept=".csv,text/csv"
              id="gocardless-csv-file"
              type="file"
              onChange={handleFileChange}
              disabled={isContextLocked}
            />
          </div>
          {file && (
            <Typography variant="body2" color="textSecondary">
              Selected file:
              {' '}
              {file.name}
            </Typography>
          )}

          {fileError && (
            <div className={classes.messageSpacing}>
              <Alert severity="error">{fileError}</Alert>
            </div>
          )}

          <div className={classes.sampleCsvHelperRow}>
            <button
              type="button"
              className={classes.sampleCsvLink}
              onClick={() => setSampleCsvDialogOpen(true)}
            >
              View sample CSV
            </button>
          </div>

          <Dialog
            open={sampleCsvDialogOpen}
            onClose={() => setSampleCsvDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Sample subscription import CSV
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Example only — IDs and dates are illustrative. Your file should use the same header columns.
              </Typography>
              <TableContainer component={Paper} className={classes.sampleCsvTableWrap}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {map(SAMPLE_SUBSCRIPTION_IMPORT_TABLE.headers, (header, i) => (
                        <TableCell key={`h-${i}`}>{header}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {map(SAMPLE_SUBSCRIPTION_IMPORT_TABLE.rows, (row, rowIndex) => (
                      <TableRow key={`r-${rowIndex}`}>
                        {map(row, (cell, cellIndex) => (
                          <TableCell key={`c-${rowIndex}-${cellIndex}`}>{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSampleCsvDialogOpen(false)} color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {validationRowEstimateWarning && (
            <div className={classes.messageSpacing}>
              <Alert severity="warning">{validationRowEstimateWarning}</Alert>
            </div>
          )}

          {isValidating && (
            <div className={classes.validationProgress}>
              <CircularProgress size={26} thickness={2} className={classes.validationProgressSpinner} />
              <div>
                <Typography variant="body2">
                  Checking your file…
                </Typography>
                {validatePollStatus && isJobProcessing(validatePollStatus) && (
                  <Typography variant="body2" color="textSecondary">
                    Status:
                    {' '}
                    {getStatusLabel(validatePollStatus)}
                  </Typography>
                )}
              </div>
            </div>
          )}

          {validationError && (
            <div className={classes.messageSpacing}>
              <Alert
                severity="error"
                variant={
                  validationErrorCode === SUBSCRIPTION_IMPORT_ROW_LIMIT_ERROR_CODE
                    ? 'filled'
                    : 'standard'
                }
              >
                {validationError}
              </Alert>
            </div>
          )}

          {validation?.valid && !hasRowValidationErrors(validation) && !hasImportStarted && (
            <div className={classes.messageSpacing}>
              <Alert severity="success">
                Validation passed (
                {validation.row_count || 0}
                {' '}
                rows). You can now run the migration.
              </Alert>
            </div>
          )}

          {validation?.valid && hasRowValidationErrors(validation) && (
            <div className={classes.messageSpacing}>
              <Alert severity="warning">
                Validation found issues:
                {' '}
                {rowErrorMessage}
              </Alert>
            </div>
          )}

          {validation && validation.valid === false && (
            <div className={classes.messageSpacing}>
              <Alert severity="error">
                {validation.row_errors_truncated && validation.row_errors_truncation_message && (
                  <>
                    {validation.row_errors_truncation_message}
                    {' '}
                  </>
                )}
                {rowErrorMessage || validation.error || 'Validation failed. Please fix the CSV and retry.'}
              </Alert>
            </div>
          )}

          {!hasSuccessfulValidation && (
            <div className={classes.actionRow}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleValidate}
                disabled={isValidating || !file || !hasAccountContext}
              >
                {isValidating ? 'Validation in progress…' : 'Validate'}
              </Button>
            </div>
          )}

          {(hasSuccessfulValidation || hasImportActivity) && (
            <>
              {!hasImportStarted && (
                <div className={classes.actionRow}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleImportStart}
                    disabled={!canStartImport}
                  >
                    {isImportStarting ? 'Starting migration...' : 'Run migration'}
                  </Button>
                </div>
              )}

              {hasImportActivity && (
                <div className={classes.messageSpacing}>
                  {(isImportPolling || importStatus) && (
                    <div>
                      <LinearProgress
                        variant={isImportPolling ? 'indeterminate' : 'determinate'}
                        value={isImportPolling ? 0 : migrationProgressBarValue}
                      />
                      <Typography variant="body2" color="textSecondary" className={classes.progressCopy}>
                        Status:
                        {' '}
                        {getStatusLabel(importStatus)}
                      </Typography>
                    </div>
                  )}

                  {importError && (
                    <div className={classes.messageSpacing}>
                      <Alert
                        severity="error"
                        variant={
                          importErrorCode === SUBSCRIPTION_IMPORT_ROW_LIMIT_ERROR_CODE
                            ? 'filled'
                            : 'standard'
                        }
                      >
                        {importError}
                      </Alert>
                      <Typography variant="body2" color="textSecondary" className={classes.messageSpacing}>
                        To retry, start over and validate the file again.
                      </Typography>
                    </div>
                  )}

                  {downloadError && (
                    <div className={classes.messageSpacing}>
                      <Alert severity="warning">
                        Could not load the results download link:
                        {' '}
                        {downloadError}
                      </Alert>
                    </div>
                  )}

                  {importSummary && (
                    <div className={classes.messageSpacing}>
                      <Alert severity="success">
                        Complete:
                        {' '}
                        {importSummary.success || 0}
                        {' '}
                        success,
                        {' '}
                        {importSummary.errors || 0}
                        {' '}
                        errors,
                        {' '}
                        {importSummary.total || 0}
                        {' '}
                        total.
                      </Alert>
                    </div>
                  )}

                  {downloadUrl && (
                    <div className={classes.actionRow}>
                      <Button
                        variant="contained"
                        color="primary"
                        href={downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download results CSV
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

        </CardContent>
      </Card>

      {/*
      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Recent migrations (last 30 days)
          </Typography>

          {recentRunsError && (
            <div className={classes.messageSpacing}>
              <Alert severity="warning">
                Could not load recent migrations:
                {' '}
                {recentRunsError}
              </Alert>
            </div>
          )}

          {recentRuns.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No recent migrations found.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Run</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Success</TableCell>
                  <TableCell>Errors</TableCell>
                  <TableCell>Download</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {map(recentRuns, (run) => (
                  <TableRow key={run.import_run_id || run.id}>
                    <TableCell>{run.import_run_id || run.id}</TableCell>
                    <TableCell>{run.status || '-'}</TableCell>
                    <TableCell>{run.summary?.success ?? '-'}</TableCell>
                    <TableCell>{run.summary?.errors ?? '-'}</TableCell>
                    <TableCell>
                      {run.download_url ? (
                        <Link href={run.download_url} target="_blank" rel="noopener noreferrer">
                          CSV
                        </Link>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      */}
    </div>
  );
};

GoCardlessMigration.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.object,
  }).isRequired,
};

const styles = (theme) => ({
  sectionCard: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: 0,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  inputRow: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  actionRow: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  validationProgress: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  validationProgressSpinner: {
    marginRight: theme.spacing(1.5),
  },
  messageSpacing: {
    marginTop: theme.spacing(2),
  },
  progressCopy: {
    marginTop: theme.spacing(1),
  },
  accountSearchHeading: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  accountSearchField: {
    marginBottom: theme.spacing(1),
  },
  accountSearchHint: {
    marginTop: theme.spacing(0.5),
  },
  accountSearchStatus: {
    minHeight: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
  },
  accountSearchSpinner: {
    marginTop: theme.spacing(0.5),
  },
  accountSearchResult: {
    marginTop: theme.spacing(0.5),
  },
  selectedAccountCard: {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.divider,
  },
  selectedAccountContent: {
    padding: theme.spacing(1.5),
    '&:last-child': {
      paddingBottom: theme.spacing(1.5),
    },
  },
  selectedAccountHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  selectedAccountLabel: {
    flex: 1,
    minWidth: 0,
    paddingTop: theme.spacing(0.25),
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium,
  },
  changeAccountButton: {
    flexShrink: 0,
  },
  sampleCsvLink: {
    ...theme.typography.body2,
    fontWeight: theme.typography.fontWeightRegular,
    margin: 0,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: theme.palette.primary.main,
    textDecoration: 'none',
    verticalAlign: 'baseline',
    '&:hover': {
      color: theme.palette.primary.dark,
      textDecoration: 'underline',
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  sampleCsvHelperRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
  },
  sampleCsvTableWrap: {
    marginTop: theme.spacing(2),
    maxHeight: 360,
    overflow: 'auto',
  },
});

export default withStyles(styles)(GoCardlessMigration);
